// Snippet Reference Panel - Quick reference for available triggers

import { useState, useMemo } from "react";
import katex from "katex";
import { allMathTriggers } from "../triggers";
import type { TriggerDefinition } from "../triggers";

// Document-level triggers (Tab-triggered in PaperEditor)
interface DocTrigger {
    trigger: string;
    name: string;
    description: string;
}

const documentTriggers: { category: string; triggers: DocTrigger[] }[] = [
    {
        category: "Sections",
        triggers: [
            { trigger: "scn", name: "Section", description: "Heading level 1" },
            { trigger: "sec", name: "Section", description: "Heading level 1 (alias)" },
            { trigger: "sbn", name: "Subsection", description: "Heading level 2" },
            { trigger: "ssec", name: "Subsection", description: "Heading level 2 (alias)" },
            { trigger: "ssn", name: "Subsubsection", description: "Heading level 3" },
            { trigger: "sssec", name: "Subsubsection", description: "Heading level 3 (alias)" },
        ],
    },
    {
        category: "Theorems (Short)",
        triggers: [
            { trigger: "oo", name: "Theorem", description: "Theorem environment" },
            { trigger: "ll", name: "Lemma", description: "Lemma environment" },
            { trigger: "ps", name: "Proposition", description: "Proposition environment" },
            { trigger: "cc", name: "Corollary", description: "Corollary environment" },
            { trigger: "dd", name: "Definition", description: "Definition environment" },
            { trigger: "re", name: "Remark", description: "Remark environment" },
            { trigger: "ee", name: "Example", description: "Example environment" },
            { trigger: "pf", name: "Proof", description: "Proof environment" },
            { trigger: "ex", name: "Exercise", description: "Exercise environment" },
        ],
    },
    {
        category: "Theorems (Long)",
        triggers: [
            { trigger: "thm", name: "Theorem", description: "Theorem environment" },
            { trigger: "lem", name: "Lemma", description: "Lemma environment" },
            { trigger: "prop", name: "Proposition", description: "Proposition environment" },
            { trigger: "cor", name: "Corollary", description: "Corollary environment" },
            { trigger: "defn", name: "Definition", description: "Definition environment" },
        ],
    },
    {
        category: "Math",
        triggers: [
            { trigger: "mm", name: "Inline Math", description: "Inline math with spacing" },
            { trigger: "dm", name: "Display Math", description: "Display math block" },
            { trigger: "eq", name: "Equation", description: "Numbered equation" },
            { trigger: "ali", name: "Align", description: "Align environment" },
        ],
    },
    {
        category: "Lists",
        triggers: [
            { trigger: "tz", name: "Bullet List", description: "Itemize (bullet points)" },
            { trigger: "item", name: "Bullet List", description: "Itemize (alias)" },
            { trigger: "enn", name: "Numbered List", description: "Enumerate (numbered)" },
            { trigger: "enum", name: "Numbered List", description: "Enumerate (alias)" },
            { trigger: "enI", name: "Roman (caps)", description: "Enumerate I, II, III" },
            { trigger: "eni", name: "Roman (lower)", description: "Enumerate i, ii, iii" },
            { trigger: "enA", name: "Alpha (caps)", description: "Enumerate A, B, C" },
            { trigger: "ena", name: "Alpha (lower)", description: "Enumerate a, b, c" },
            { trigger: "tm", name: "New Item", description: "\\item (split list item)" },
        ],
    },
    {
        category: "Tables & Figures",
        triggers: [
            { trigger: "t<r>b<c>l", name: "Dynamic Table", description: "Table with r rows, c cols (e.g. t3b5l)" },
            { trigger: "tbl", name: "Table", description: "Default 2×2 table" },
            { trigger: "fig", name: "Figure", description: "Figure with includegraphics" },
            { trigger: "tikz", name: "TikZ Picture", description: "TikZ diagram block" },
        ],
    },
    {
        category: "Code & Other",
        triggers: [
            { trigger: "vb", name: "Verbatim", description: "Code block" },
            { trigger: "code", name: "Code Block", description: "Code block (alias)" },
            { trigger: "en", name: "Generic Env", description: "Custom environment" },
            { trigger: "tl", name: "Title", description: "Document title" },
            { trigger: "aut", name: "Author", description: "Document author" },
            { trigger: "abs", name: "Abstract", description: "Document abstract" },
        ],
    },
];

// Convert a trigger's latex to a preview-friendly version
function getPreviewLatex(latex: string): string {
    // Replace placeholders with example content
    let preview = latex
        .replace(/#0/g, "a")
        .replace(/#1/g, "b")
        .replace(/#2/g, "c")
        .replace(/#3/g, "d")
        .replace(/#\?/g, "\\square");

    // For structural commands that need content, add minimal example
    if (preview === "_{a}") preview = "x_{a}";
    if (preview === "^{a}") preview = "x^{a}";
    if (preview === "_{a}^{b}") preview = "x_{a}^{b}";

    return preview;
}

// Render LaTeX to HTML safely
function renderLatexPreview(latex: string): string {
    try {
        const preview = getPreviewLatex(latex);
        return katex.renderToString(preview, {
            throwOnError: false,
            displayMode: false,
            strict: "ignore",
            trust: true,
        });
    } catch {
        return "";
    }
}

// Map internal category names to display names
const categoryDisplayNames: Record<string, string> = {
    "math-font": "Math Fonts",
    fraction: "Fractions",
    subscript: "Subscript/Superscript",
    superscript: "Subscript/Superscript",
    delimiter: "Delimiters",
    matrix: "Matrices",
    greek: "Greek Letters",
    function: "Functions",
    operator: "Operators",
    relation: "Relations",
    set: "Set Theory",
    logic: "Logic",
    arrow: "Arrows",
    accent: "Accents (prefix)",
    postfix: "Accents (postfix — type letter first)",
    decoration: "Decorations",
    integral: "Integrals",
    derivative: "Derivatives",
    limit: "Limits",
    "sum-product": "Sums & Products",
    symbol: "Symbols",
    ellipsis: "Ellipses",
    spacing: "Spacing",
    display: "Display",
    numbering: "Numbering",
    cases: "Cases",
    compound: "Compound",
};

// Order categories should appear in
const categoryOrder = [
    "fraction",
    "subscript",
    "delimiter",
    "matrix",
    "greek",
    "function",
    "operator",
    "relation",
    "set",
    "logic",
    "integral",
    "derivative",
    "limit",
    "sum-product",
    "arrow",
    "accent",
    "postfix",
    "decoration",
    "math-font",
    "symbol",
    "ellipsis",
    "spacing",
    "display",
    "cases",
    "compound",
    "numbering",
];

interface SnippetReferenceProps {
    isOpen: boolean;
    onClose: () => void;
}

export function SnippetReference({ isOpen, onClose }: SnippetReferenceProps) {
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
        new Set(["Sections", "Theorems (Short)", "math-entry", "fraction", "delimiter", "greek"])
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState<"document" | "math">("math");

    // Group triggers by category (merge subscript/superscript)
    const groupedTriggers = useMemo(() => {
        const groups: Record<string, TriggerDefinition[]> = {};

        for (const trigger of allMathTriggers) {
            // Skip regex-only triggers that don't have meaningful display
            if (trigger.isRegex && !trigger.trigger) continue;

            // Merge superscript into subscript category
            const category =
                trigger.category === "superscript" ? "subscript" : trigger.category;

            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(trigger);
        }

        return groups;
    }, []);

    // Filter triggers by search query
    const filteredGroups = useMemo(() => {
        if (!searchQuery.trim()) return groupedTriggers;

        const query = searchQuery.toLowerCase();
        const filtered: Record<string, TriggerDefinition[]> = {};

        for (const [category, triggers] of Object.entries(groupedTriggers)) {
            const matching = triggers.filter(
                (t) =>
                    t.trigger.toLowerCase().includes(query) ||
                    t.name.toLowerCase().includes(query) ||
                    t.latex.toLowerCase().includes(query)
            );
            if (matching.length > 0) {
                filtered[category] = matching;
            }
        }

        return filtered;
    }, [groupedTriggers, searchQuery]);

    // Math entry-point triggers (mm, dm, eq) for the Math tab header
    const mathEntryTriggers = useMemo(() => {
        const entries = [
            { trigger: "mm", name: "Inline math", preview: "$...$" },
            { trigger: "dm", name: "Display math", preview: "\[...\]" },
            { trigger: "eq", name: "Numbered equation", preview: "\[...\] (1)" },
        ];
        if (!searchQuery.trim()) return entries;
        const query = searchQuery.toLowerCase();
        return entries.filter(
            (e) => e.trigger.includes(query) || e.name.toLowerCase().includes(query)
        );
    }, [searchQuery]);

    // Filter document triggers by search query
    const filteredDocTriggers = useMemo(() => {
        if (!searchQuery.trim()) return documentTriggers;

        const query = searchQuery.toLowerCase();
        return documentTriggers
            .map((group) => ({
                ...group,
                triggers: group.triggers.filter(
                    (t) =>
                        t.trigger.toLowerCase().includes(query) ||
                        t.name.toLowerCase().includes(query) ||
                        t.description.toLowerCase().includes(query)
                ),
            }))
            .filter((group) => group.triggers.length > 0);
    }, [searchQuery]);

    // Sort categories by defined order
    const sortedCategories = useMemo(() => {
        const categories = Object.keys(filteredGroups);
        return categories.sort((a, b) => {
            const indexA = categoryOrder.indexOf(a);
            const indexB = categoryOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    }, [filteredGroups]);

    const toggleCategory = (category: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    if (!isOpen) return null;

    return (
        <div className="snippet-reference">
            <div className="snippet-reference-header">
                <h2>Snippet Reference</h2>
                <button className="snippet-close-btn" onClick={onClose} title="Close">
                    ×
                </button>
            </div>

            <div className="snippet-tabs">
                <button
                    className={`snippet-tab ${activeTab === "document" ? "active" : ""}`}
                    onClick={() => setActiveTab("document")}
                >
                    Document (Tab)
                </button>
                <button
                    className={`snippet-tab ${activeTab === "math" ? "active" : ""}`}
                    onClick={() => setActiveTab("math")}
                >
                    Math Mode
                </button>
            </div>

            <div className="snippet-search">
                <input
                    type="text"
                    placeholder="Search snippets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            <div className="snippet-reference-content">
                {activeTab === "document" ? (
                    <>
                        {filteredDocTriggers.map((group) => (
                            <div key={group.category} className="snippet-category">
                                <button
                                    className="snippet-category-header"
                                    onClick={() => toggleCategory(group.category)}
                                >
                                    <span className="snippet-category-icon">
                                        {expandedCategories.has(group.category) ? "▼" : "▶"}
                                    </span>
                                    <span className="snippet-category-name">
                                        {group.category}
                                    </span>
                                    <span className="snippet-category-count">
                                        {group.triggers.length}
                                    </span>
                                </button>

                                {expandedCategories.has(group.category) && (
                                    <div className="snippet-list">
                                        {group.triggers.map((trigger) => (
                                            <div key={trigger.trigger} className="snippet-item">
                                                <code className="snippet-trigger">{trigger.trigger}</code>
                                                <span className="snippet-name">{trigger.name}</span>
                                                <span className="snippet-description">
                                                    {trigger.description}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {filteredDocTriggers.length === 0 && searchQuery && (
                            <div className="snippet-no-results">
                                No document snippets match "{searchQuery}"
                            </div>
                        )}

                        {/* Table patterns note */}
                        <div className="snippet-note">
                            <strong>Table Pattern:</strong>
                            <p>
                                <code>t&lt;rows&gt;b&lt;cols&gt;l</code> creates an N×M table
                            </p>
                            <ul>
                                <li><code>t2b3l</code> → 2 rows × 3 columns</li>
                                <li><code>t4b4l</code> → 4 rows × 4 columns</li>
                                <li><code>tbl</code> → default 2×2 table</li>
                            </ul>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Math-mode-only banner */}
                        <div className="snippet-note" style={{ marginBottom: 8 }}>
                            <strong>Math mode only</strong> — all triggers below fire automatically
                            as you type inside a math block ($\inline$ or display math).
                            No Tab needed. Use the <strong>Math</strong> entry triggers on the
                            left to open a math block first.
                        </div>

                        {/* Math entry-point triggers (document-level, not math-mode) */}
                        {mathEntryTriggers.length > 0 && (
                            <div className="snippet-category">
                                <button
                                    className="snippet-category-header"
                                    onClick={() => toggleCategory("math-entry")}
                                >
                                    <span className="snippet-category-icon">
                                        {expandedCategories.has("math-entry") ? "▼" : "▶"}
                                    </span>
                                    <span className="snippet-category-name">Math Entry</span>
                                    <span className="snippet-category-count">{mathEntryTriggers.length}</span>
                                </button>
                                {expandedCategories.has("math-entry") && (
                                    <div className="snippet-list">
                                        {mathEntryTriggers.map((e) => (
                                            <div key={e.trigger} className="snippet-item">
                                                <code className="snippet-trigger">{e.trigger}</code>
                                                <span className="snippet-name">{e.name}</span>
                                                <span className="snippet-preview" style={{ fontStyle: "italic", opacity: 0.6 }}>
                                                    {e.preview}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {sortedCategories.map((category) => (
                            <div key={category} className="snippet-category">
                                <button
                                    className="snippet-category-header"
                                    onClick={() => toggleCategory(category)}
                                >
                                    <span className="snippet-category-icon">
                                        {expandedCategories.has(category) ? "▼" : "▶"}
                                    </span>
                                    <span className="snippet-category-name">
                                        {categoryDisplayNames[category] || category}
                                    </span>
                                    <span className="snippet-category-count">
                                        {filteredGroups[category].length}
                                    </span>
                                </button>

                                {expandedCategories.has(category) && (
                                    <div className="snippet-list">
                                        {filteredGroups[category].map((trigger) => (
                                            <div key={trigger.id} className="snippet-item">
                                                <code className="snippet-trigger">{trigger.trigger}</code>
                                                <span className="snippet-name">{trigger.name}</span>
                                                <span
                                                    className="snippet-preview"
                                                    dangerouslySetInnerHTML={{
                                                        __html: renderLatexPreview(trigger.latex)
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {sortedCategories.length === 0 && searchQuery && (
                            <div className="snippet-no-results">
                                No math snippets match "{searchQuery}"
                            </div>
                        )}

                        {/* Matrix patterns note */}
                        <div className="snippet-note">
                            <strong>Matrix Patterns:</strong>
                            <ul>
                                <li>
                                    <code>[type][rows]x[cols]</code> — e.g., <code>p2x3</code> → 2×3 pmatrix
                                </li>
                                <li>
                                    <code>[type][rows]h[cols]</code> — homogeneous (a₁₁, a₁₂, ...)
                                </li>
                                <li>
                                    <code>[type]gn</code> / <code>[type]gs</code> — generic matrix (⋯ and ⋮)
                                </li>
                                <li>
                                    <code>[type]gd</code> — dotted matrix template
                                </li>
                                <li>
                                    <code>[type]gr</code> / <code>[type]gc</code> — row/column vector
                                </li>
                                <li>
                                    <code>[n]cs</code> — cases with n rows (e.g., <code>3cs</code>)
                                </li>
                            </ul>
                            <p>
                                Types: <code>p</code> (paren), <code>b</code> [bracket],{" "}
                                <code>B</code> {"{"}brace{"}"}, <code>v</code> |vbar|, <code>V</code> ‖double‖
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
